import {
    Dialog, DialogTitle, DialogContent, DialogActions, Typography,
    TextField, Button, Grid, Snackbar, Alert, MenuItem
} from '@mui/material';
import { useEffect, useState } from 'react';
import axios from 'axios';
import urls from '../../Mobx/Urls';
import permissionProfileStore from '../../Mobx/PermissionProfileStore';
import UpdateUserGoal from './UpdateUserGoal';

const UpdateUser = ({ user, onClose, onSuccess }) => {
    const [form, setForm] = useState(null);
    const [originalForm, setOriginalForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [enterSerial, setEnterSerial] = useState(false);
    const [isUpdateUserGoalOpen, setIsUpdateUserGoalOpen] = useState(false);

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        if (user) {
            const initial = {
                _id: user._id,
                firstName: user.firstName || '',
                lastName: user.lastName || '',
                role: user.role || '',
                subscriptionLevel: user.subscriptionLevel ?? '',
                typeOfDictionary: user.typeOfDictionary || '',
                permissionProfile: user.permissionProfile?._id || ''
            };
            setForm(initial);
            setOriginalForm(initial);
            setErrors({});
            setEnterSerial(false);
        }
    }, [user]);

    useEffect(() => {
        permissionProfileStore.fetchProfiles({
            page: 1,
            limit: 1000,
            filters: {},
            sort: [{ field: 'name', order: 'asc' }]
        });
    }, []);

    const handleChange = (key, value) => {
        setForm(prev => ({ ...prev, [key]: value }));
        if (value.trim?.() || value === 0) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
        if (key === 'subscriptionLevel') {
            if (value === -2) {
                setEnterSerial(true);
            } else {
                setEnterSerial(false);
            }
        }
    };

    const handleSubmit = async () => {
        const requiredFields = ['firstName', 'lastName', 'role', 'subscriptionLevel', 'typeOfDictionary'];
        if (enterSerial) {
            requiredFields.push('serialCode');
        }
        const newErrors = {};
        requiredFields.forEach(key => {
            if (!form[key]?.toString().trim()) newErrors[key] = 'שדה חובה';
        });
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;



        // שליחה רק של שדות ששונו + תמיד לשלוח _id
        const changedFields = { _id: form._id };
        for (const key of Object.keys(form)) {
            if (key !== '_id' && form[key] !== originalForm[key]) {
                changedFields[key] = form[key];
            }
        }




        // אם רק _id נשלח – כלומר לא בוצע שינוי אחר
        if (Object.keys(changedFields).length === 1) {
            showSnackbar('לא בוצע שינוי.', 'info');
            return;
        }

        if (changedFields.permissionProfile === '') {
            changedFields.permissionProfile = null;
        }

        try {
            setIsSubmitting(true);
            if (changedFields.subscriptionLevel !== undefined) {
                if (changedFields.subscriptionLevel === -2 && changedFields.serialCode) {
                    await axios.post(urls.accessSerial.apply, {
                        userId: form._id,
                        serial: form.serialCode
                    }, { withCredentials: true });
                    delete changedFields.serialCode;
                    delete changedFields.subscriptionLevel;
                } else if (originalForm.subscriptionLevel === -2 && changedFields.subscriptionLevel !== -2) {
                    await axios.delete(`${urls.accessSerial.revoke}/${form._id}`, { withCredentials: true });
                }
            }
            await axios.patch(urls.user.update, changedFields, { withCredentials: true });
            onSuccess();
            onClose();
        } catch (err) {
            showSnackbar(
                <>
                    שגיאה בעדכון המשתמש:<br />
                    {err?.toString()}<br />
                    {err?.response?.data}
                </>,
                'error'
            );

        } finally {
            setIsSubmitting(false);
        }
    };

    if (!form) return null;

    return (
        <>
            <Dialog open={!!user} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    עריכת משתמש
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 0.5, minHeight: '25px' }}>
                        {user?.email}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="שם פרטי"
                                value={form.firstName}
                                onChange={e => handleChange('firstName', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.firstName)}
                                helperText={errors.firstName}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="שם משפחה"
                                value={form.lastName}
                                onChange={e => handleChange('lastName', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.lastName)}
                                helperText={errors.lastName}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="תפקיד"
                                select
                                value={form.role}
                                onChange={e => handleChange('role', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.role)}
                                helperText={errors.role}
                            >
                                {['teacher', 'student', 'admin', 'withoutRole'].map(val => (
                                    <MenuItem key={val} value={val}>
                                        {{
                                            teacher: 'מורה',
                                            student: 'תלמיד',
                                            admin: 'מנהל',
                                            withoutRole: 'ללא תפקיד'
                                        }[val]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={enterSerial ? 6 : 12} md={enterSerial ? 3 : 6}>
                            <TextField
                                label="גישה"
                                select
                                value={form.subscriptionLevel}
                                onChange={e => handleChange('subscriptionLevel', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.subscriptionLevel)}
                                helperText={errors.subscriptionLevel}
                            >
                                {[
                                    [-2, 'קוד סיריאל'],
                                    [-1, 'עובד החברה'],
                                    [0, 'חסום'],
                                    [1, 'משלם']
                                ].map(([val, label]) => (
                                    <MenuItem key={val} value={val}>{label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        {enterSerial &&
                            <Grid item xs={6} md={3}>
                                <TextField
                                    label="קוד סיריאל"
                                    value={form.serialCode || ''}
                                    onChange={e => handleChange('serialCode', e.target.value)}
                                    fullWidth
                                    error={Boolean(errors.serialCode)}
                                    helperText={errors.serialCode}
                                >
                                </TextField>
                            </Grid>
                        }
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="סוג מילון"
                                select
                                value={form.typeOfDictionary}
                                onChange={e => handleChange('typeOfDictionary', e.target.value)}
                                fullWidth
                                required
                                error={Boolean(errors.typeOfDictionary)}
                                helperText={errors.typeOfDictionary}
                            >
                                {['universal', 'olami'].map(val => (
                                    <MenuItem key={val} value={val}>
                                        {{ universal: 'אוניברסלי', olami: 'עולמי' }[val]}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="פרופיל הרשאות"
                                select
                                value={form.permissionProfile || ''}
                                onChange={e => handleChange('permissionProfile', e.target.value)}
                                fullWidth
                                error={Boolean(errors.permissionProfile)}
                                helperText={errors.permissionProfile}
                            >
                                <MenuItem value="">ללא</MenuItem>
                                {permissionProfileStore.profiles.map(profile => (
                                    <MenuItem key={profile._id} value={profile._id}>
                                        {profile.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsUpdateUserGoalOpen(true)} disabled={isSubmitting}>כיתות</Button>
                    <Button onClick={onClose} disabled={isSubmitting}>ביטול</Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>שמירה</Button>
                </DialogActions>
            </Dialog>
            <UpdateUserGoal
                user={user}
                isOpen={isUpdateUserGoalOpen}
                onClose={() => setIsUpdateUserGoalOpen(false)}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={null}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default UpdateUser;
